import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Portal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useAppStore } from '@/store/appStore'
import { useFeedbackContext } from '@/hooks/useFeedbackContext'
import { useSubmitFeedback } from '@/hooks/useSubmitFeedback'
import type { FeedbackType } from '@/types/api'

import { useIsCompact } from '@/hooks/useLayoutMode'

const WIDTH = 460
const MARGIN = 12

const FEEDBACK_TYPES: Array<{ value: FeedbackType; labelKey: string }> = [
  { value: 'SpellingGrammar', labelKey: 'feedback.typeSpellingGrammar' },
  { value: 'Content', labelKey: 'feedback.typeContent' },
  { value: 'AppFunctionality', labelKey: 'feedback.typeAppFunctionality' },
  { value: 'Other', labelKey: 'feedback.typeOther' },
]

interface Position {
  x: number
  y: number
}

/** Keep the panel fully on screen after a drag or a window resize. */
function clamp(pos: Position, el: HTMLElement | null): Position {
  const width = el?.offsetWidth ?? WIDTH
  const height = el?.offsetHeight ?? 0
  return {
    x: Math.min(Math.max(MARGIN, pos.x), Math.max(MARGIN, window.innerWidth - width - MARGIN)),
    y: Math.min(Math.max(MARGIN, pos.y), Math.max(MARGIN, window.innerHeight - height - MARGIN)),
  }
}

/**
 * Non-modal feedback panel. It is deliberately NOT a MUI Dialog: the user keeps full access to
 * the app underneath (no backdrop, no focus trap) and can drag the panel out of the way. It only
 * closes on an explicit action: the X, Cancel, or Close after a successful submission.
 */
export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const isOffline = useAppStore((s) => s.isOffline)
  const buildContext = useFeedbackContext()
  const { submit, isSubmitting } = useSubmitFeedback()

  const paperRef = useRef<HTMLDivElement>(null)
  const dragOffset = useRef<Position | null>(null)

  // Mounted only while open (see FeedbackButton), so mount-time state IS the reset.
  // At compact the panel is docked to the bottom of the viewport and cannot be
  // dragged: there is no cursor to grab the header with and no room to move it to.
  const isCompact = useIsCompact()
  const [position, setPosition] = useState<Position>(() =>
    clamp({ x: window.innerWidth - WIDTH - 32, y: 132 }, null)
  )
  const [type, setType] = useState<FeedbackType | ''>('')
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState<'submitted' | 'queued' | null>(null)

  useEffect(() => {
    const onResize = () => setPosition((p) => clamp(p, paperRef.current))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    const rect = paperRef.current?.getBoundingClientRect()
    if (!rect) return
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }

    const handleMove = (ev: PointerEvent) => {
      if (!dragOffset.current) return
      setPosition(
        clamp({ x: ev.clientX - dragOffset.current.x, y: ev.clientY - dragOffset.current.y }, paperRef.current)
      )
    }
    const handleUp = () => {
      dragOffset.current = null
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
  }

  const handleSubmit = async () => {
    const trimmed = comment.trim()
    if (!trimmed || !type) return
    try {
      setSubmitted(await submit(type, trimmed, buildContext()))
    } catch {
      // The API's own message is English-only, so the localised string is what the user sees.
      toast.error(t('feedback.submitFailed'))
    }
  }

  return (
    <Portal>
      <Paper
        ref={paperRef}
        elevation={8}
        role="dialog"
        aria-modal={false}
        aria-label={t('feedback.title')}
        sx={isCompact
          ? {
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxHeight: '85dvh',
              overflowY: 'auto',
              borderRadius: '12px 12px 0 0',
              zIndex: 1300,
              pb: 'env(safe-area-inset-bottom, 0px)',
            }
          : {
              position: 'fixed',
              left: position.x,
              top: position.y,
              width: WIDTH,
              maxWidth: 'calc(100vw - 24px)',
              zIndex: 1300,
              overflow: 'hidden',
            }}
      >
        <Box
          onPointerDown={isCompact ? undefined : handlePointerDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1,
            cursor: isCompact ? 'default' : 'move',
            userSelect: 'none',
            touchAction: isCompact ? 'auto' : 'none',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {!isCompact && <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.8 }} />}
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {t('feedback.title')}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('feedback.close')}
            sx={{ color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {submitted ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="body2">
                  {submitted === 'queued' ? t('feedback.queuedMessage') : t('feedback.successMessage')}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={onClose}>
                  {t('feedback.close')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {t('feedback.intro')}
              </Typography>

              {/* Type picker, with the content-accuracy caveat sitting beside the group. */}
              <FormControl>
                <FormLabel id="feedback-type-label" sx={{ fontSize: 13, mb: 0.5 }}>
                  {t('feedback.typeLabel')}
                </FormLabel>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <RadioGroup
                    aria-labelledby="feedback-type-label"
                    value={type}
                    onChange={(e) => setType(e.target.value as FeedbackType)}
                  >
                    {FEEDBACK_TYPES.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                        label={
                          <Typography variant="body2">{t(option.labelKey)}</Typography>
                        }
                      />
                    ))}
                  </RadioGroup>

                  {type === 'Content' && (
                    <Alert
                      severity="warning"
                      sx={{
                        flex: 1,
                        alignSelf: 'center',
                        py: 0.25,
                        '& .MuiAlert-message': { fontSize: 12, py: 0.5 },
                        '& .MuiAlert-icon': { fontSize: 18, mr: 1 },
                      }}
                    >
                      {t('feedback.contentTypeWarning')}
                    </Alert>
                  )}
                </Box>
              </FormControl>

              {isOffline && <Alert severity="info">{t('feedback.offlineNotice')}</Alert>}

              <TextField
                label={t('feedback.commentLabel')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline
                minRows={4}
                maxRows={12}
                fullWidth
                autoFocus
                slotProps={{ htmlInput: { maxLength: 4000 } }}
              />

              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  {t('feedback.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !type || comment.trim().length === 0}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {t('feedback.submit')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Portal>
  )
}
